"use client"

import { useState, useEffect } from "react"
import { db, storage } from "@/lib/firebase"
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, serverTimestamp, getDoc, setDoc } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage"
import { Plus, Pencil, Trash2, X, Upload, Loader2, ArrowUp, ArrowDown, User, Award, Briefcase, Image as ImageIcon, Eye, EyeOff, CheckCircle2 } from "lucide-react"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import MediaLibrary from "./media-library"

export default function StaffManager() {
  const [staff, setStaff] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploadingPoster, setIsUploadingPoster] = useState(false)
  const [posterPreview, setPosterPreview] = useState<string | null>(null)

  const [name, setName] = useState("")
  const [subject, setSubject] = useState("")
  const [description, setDescription] = useState("")
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [editingStaff, setEditingStaff] = useState<any>(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [selectingFor, setSelectingFor] = useState<"staff" | "poster">("staff")

  useEffect(() => {
    fetchStaff()
    fetchPoster()
  }, [])

  const fetchPoster = async () => {
    const snap = await getDoc(doc(db, "settings", "faculty_poster"))
    if (snap.exists()) setPosterPreview(snap.data().url)
  }

  const fetchStaff = async () => {
    try {
      const q = query(collection(db, "staff"), orderBy("orderIndex", "asc"))
      const snapshot = await getDocs(q)
      setStaff(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error(error)
      toast.error("Failed to fetch staff")
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setName("")
    setSubject("")
    setDescription("")
    setImageFile(null)
    setEditingStaff(null)
    setShowForm(false)
  }

  const handleEdit = (member: any) => {
    setEditingStaff(member)
    setName(member.name || "")
    setSubject(member.subject || "")
    setDescription(member.description || "")
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || (!imageFile && !editingStaff?.imageUrl)) {
      toast.error("Please fill all required fields")
      return
    }

    setIsSubmitting(true)
    try {
      let imageUrl = editingStaff?.imageUrl || ""

      if (imageFile) {
        // Delete old image if updating
        if (editingStaff?.imageUrl && editingStaff.imageUrl.includes("firebasestorage.googleapis.com")) {
          try {
            const oldImageRef = ref(storage, editingStaff.imageUrl)
            await deleteObject(oldImageRef)
          } catch (e) { console.error("Old image not found", e) }
        }
        const fileRef = ref(storage, `staff/${Date.now()}_${imageFile.name}`)
        await uploadBytes(fileRef, imageFile)
        imageUrl = await getDownloadURL(fileRef)
        
        await addDoc(collection(db, "media"), {
           title: `${name} Staff Profile`,
           url: imageUrl,
           type: "image",
           folder: "images",
           createdAt: serverTimestamp(),
        })
      }

      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')

      const data = {
        name: name || "",
        slug,
        subject: subject || "",
        description: description || "",
        imageUrl: imageUrl || "",
        updatedAt: serverTimestamp(),
        createdAt: (editingStaff && editingStaff.id) ? editingStaff.createdAt : serverTimestamp(),
        orderIndex: (editingStaff && editingStaff.id) ? editingStaff.orderIndex : staff.length,
        isVisible: true
      }

      if (editingStaff?.id) {
        const existing = staff.find(s => s.id === editingStaff.id)
        await updateDoc(doc(db, "staff", editingStaff.id), {
          ...data,
          isVisible: existing?.isVisible !== undefined ? existing.isVisible : true
        })
        toast.success("Staff member updated")
      } else {
        await addDoc(collection(db, "staff"), data)
        toast.success("Staff member added")
      }
      resetForm()
      fetchStaff()
    } catch (error) {
      console.error(error)
      toast.error("Failed to save staff member")
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleVisibility = async (member: any) => {
    try {
      const newStatus = !member.isVisible
      await updateDoc(doc(db, "staff", member.id), { isVisible: newStatus })
      toast.success(newStatus ? "Staff visible" : "Staff hidden")
      setStaff(prev => prev.map(m => m.id === member.id ? { ...m, isVisible: newStatus } : m))
    } catch (error) {
      toast.error("Failed to update visibility")
    }
  }

  const handleDelete = async (member: any) => {
    if (!confirm("Are you sure you want to delete this staff member?")) return
    try {
      if (member.imageUrl && member.imageUrl.includes("firebasestorage.googleapis.com")) {
        try {
          const imageRef = ref(storage, member.imageUrl)
          await deleteObject(imageRef)
        } catch (e) {
          console.error("Could not delete from storage", e)
        }
      }
      await deleteDoc(doc(db, "staff", member.id))
      toast.success("Deleted successfully")
      fetchStaff()
    } catch (error) {
      console.error(error)
      toast.error("Delete failed")
    }
  }

  const moveMember = async (index: number, direction: 'up' | 'down') => {
    const newStaff = [...staff]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newStaff.length) return

    const [removed] = newStaff.splice(index, 1)
    newStaff.splice(targetIndex, 0, removed)

    // Batch update orderIndex
    try {
      await Promise.all(newStaff.map((member, i) => 
        updateDoc(doc(db, "staff", member.id), { orderIndex: i })
      ))
      setStaff(newStaff)
    } catch (error) {
       toast.error("Reordering failed")
    }
  }

  const handlePosterUpload = async (file: File) => {
    setIsUploadingPoster(true)
    try {
      const fileRef = ref(storage, `settings/faculty_poster_${Date.now()}`)
      await uploadBytes(fileRef, file)
      const url = await getDownloadURL(fileRef)
      await setDoc(doc(db, "settings", "faculty_poster"), { url, updatedAt: serverTimestamp() })
      
      await addDoc(collection(db, "media"), {
         title: "Faculty Poster",
         url: url,
         type: "image",
         folder: "images",
         createdAt: serverTimestamp(),
      })
      
      setPosterPreview(url)
      toast.success("Faculty Poster updated successfully")
    } catch (error) {
      console.error(error)
      toast.error("Failed to upload poster")
    } finally {
      setIsUploadingPoster(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Faculty Poster Billboard Upload Section */}
      <div className="bg-white p-6 border border-slate-100 rounded-sm shadow-sm">
        <div className="flex items-center gap-3 mb-6">
           <ImageIcon className="text-[#b48c36]" size={20} />
           <h3 className="font-serif font-bold text-lg">Primary Faculty Poster</h3>
        </div>
        
        <div className="flex flex-col md:flex-row gap-8 items-start">
           <div className="w-full md:w-1/3 aspect-[4/5] bg-slate-50 border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center relative overflow-hidden group">
              {posterPreview ? (
                <>
                  <img src={posterPreview} className="w-full h-full object-cover" alt="" />
                  <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-4">
                     <label className="cursor-pointer text-white text-[10px] font-bold uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-colors w-full text-center">
                        Upload File
                        <input type="file" className="hidden" onChange={e => e.target.files?.[0] && handlePosterUpload(e.target.files[0])} />
                     </label>
                     <button 
                        type="button"
                        onClick={() => { setSelectingFor("poster"); setIsMediaLibraryOpen(true); }}
                        className="text-white text-[10px] font-bold uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-colors w-full">
                        From Library
                     </button>
                  </div>
                </>
              ) : (
                <>
                   <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => e.target.files?.[0] && handlePosterUpload(e.target.files[0])} />
                   {isUploadingPoster ? <Loader2 className="animate-spin text-[#b48c36]" /> : <><Upload className="text-slate-300" /> <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-2">Upload Team Poster</p></>}
                </>
              )}
           </div>
           
           <div className="flex-1 space-y-4">
              <p className="text-sm text-slate-500 leading-relaxed italic">
                 "This poster will replace the individual faculty grid on the homepage. Use it for batch announcements or group faculty graphics as shown in your posters."
              </p>
              <div className="p-4 bg-[#fcfaf7] border-l-4 border-[#b48c36]">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-[#b48c36]">Dimensions Tip</p>
                 <p className="text-xs text-slate-600 mt-1">Recommended: 1200x1500px for best clarity on mobile and desktop.</p>
              </div>
           </div>
        </div>
      </div>

      <div className="flex justify-between items-center bg-white p-6 border border-slate-100 rounded-sm shadow-sm">
        <div>
          <h2 className="text-2xl font-serif font-bold text-slate-900">Staff Management</h2>
          <p className="text-sm text-slate-500">Add and manage your academy's expert mentors.</p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#b48c36] text-white px-6 py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-slate-900 transition-colors"
        >
          <Plus size={16} /> Add Staff
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-xl p-8 border border-slate-100 shadow-2xl overflow-y-auto max-h-[90vh]"
          >
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-slate-50">
              <h3 className="text-xl font-serif font-bold">{editingStaff ? 'Edit Mentor' : 'New Mentor'}</h3>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Full Name</label>
                <input 
                  type="text" value={name} onChange={e => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-3 outline-none focus:border-[#b48c36] transition-colors"
                  placeholder="e.g., Komma Rohith Sir"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Subject / Specialty</label>
                <input 
                  type="text" value={subject} onChange={e => setSubject(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-3 outline-none focus:border-[#b48c36] transition-colors"
                  placeholder="e.g., Director & Polity Expert"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Brief Bio / Credentials</label>
                <textarea 
                  rows={4} value={description} onChange={e => setDescription(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-3 outline-none focus:border-[#b48c36] transition-colors"
                  placeholder="Mentored 2,000+ IAS aspirants..."
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Profile Image</label>
                <div className="relative group flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-sm h-[200px] bg-slate-50 hover:bg-slate-100 transition-all overflow-hidden">
                   {imageFile ? (
                     <div className="text-center p-4">
                        <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={32} />
                        <p className="text-xs font-bold text-slate-600 truncate max-w-[200px]">{imageFile.name}</p>
                        <button type="button" onClick={() => setImageFile(null)} className="text-red-500 text-[10px] font-bold uppercase mt-2">Remove</button>
                     </div>
                   ) : editingStaff?.imageUrl ? (
                     <img src={editingStaff.imageUrl} className="w-full h-full object-cover" alt="" />
                   ) : (
                     <div className="text-center p-4">
                        <Upload className="text-slate-300 mb-2 group-hover:text-[#b48c36] transition-colors" size={32} />
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Select Profile Pic</p>
                     </div>
                   )}
                   <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-4">
                      <label className="cursor-pointer text-white text-[10px] font-bold uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-colors w-full text-center">
                         Upload File
                         <input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files?.[0] || null)} />
                      </label>
                      <button 
                         type="button"
                         onClick={() => { setSelectingFor("staff"); setIsMediaLibraryOpen(true); }}
                         className="text-white text-[10px] font-bold uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-colors w-full">
                         From Library
                      </button>
                   </div>
                </div>
              </div>

              <Dialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
                 <DialogContent className="w-[95vw] max-w-5xl p-0 overflow-hidden border-none bg-white">
                   <DialogHeader className="p-5 sm:p-6 bg-background border-b border-border/30">
                     <DialogTitle>Select from Media Library</DialogTitle>
                   </DialogHeader>
                   <MediaLibrary
                     allowSelection
                     initialTab="images"
                     onSelect={(media) => {
                         if (selectingFor === "staff") {
                            if (editingStaff) {
                               setEditingStaff({ ...editingStaff, imageUrl: media.url })
                            } else {
                               setEditingStaff({ imageUrl: media.url }) // No ID, so it's a new entry with an image
                            }
                            setImageFile(null)
                         } else {
                           setPosterPreview(media.url)
                           setDoc(doc(db, "settings", "faculty_poster"), { url: media.url, updatedAt: serverTimestamp() })
                        }
                        setIsMediaLibraryOpen(false)
                     }}
                   />
                 </DialogContent>
              </Dialog>

              <button 
                type="submit" disabled={isSubmitting}
                className="w-full bg-[#b48c36] text-white py-4 font-bold text-xs uppercase tracking-[0.2em] hover:bg-slate-900 transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : (editingStaff ? 'Update Profile' : 'Publish Profile')}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#b48c36]" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {staff.map((member, i) => (
            <div key={member.id} className="group bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 rounded-sm">
               <div className="aspect-square relative bg-slate-50 overflow-hidden">
                  <img src={member.imageUrl} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="" />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                     <button onClick={() => handleEdit(member)} className="p-1.5 bg-white text-[#b48c36] rounded-full hover:bg-[#b48c36] hover:text-white transition-all"><Pencil size={12} /></button>
                     <button onClick={() => toggleVisibility(member)} className={`p-1.5 bg-white rounded-full transition-all ${member.isVisible === false ? 'text-rose-500' : 'text-slate-400 hover:text-emerald-500 hover:bg-emerald-50'}`}>{member.isVisible === false ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                     <button onClick={() => handleDelete(member)} className="p-1.5 bg-white text-rose-500 rounded-full hover:bg-rose-500 hover:text-white transition-all"><Trash2 size={12} /></button>
                  </div>
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 text-[6px] font-black uppercase tracking-widest shadow-sm">
                     #{i + 1}
                  </div>
               </div>
               <div className="p-3 text-center">
                  <h4 className="font-serif font-bold text-slate-900 leading-tight mb-0.5 text-[10px] truncate px-1">{member.name}</h4>
                  <p className="text-[7px] text-[#b48c36] font-bold uppercase tracking-widest mb-2 opacity-70 truncate px-1">{member.subject}</p>
                  <div className="flex items-center justify-center gap-2">
                     <button onClick={() => moveMember(i, 'up')} disabled={i === 0} className="text-slate-400 hover:text-[#b48c36] disabled:opacity-30"><ArrowUp size={12} /></button>
                     <button onClick={() => moveMember(i, 'down')} disabled={i === staff.length - 1} className="text-slate-400 hover:text-[#b48c36] disabled:opacity-30"><ArrowDown size={12} /></button>
                  </div>
               </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
